import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import style from './style.module.scss'; // won't generate a '.d.ts' file until component is included in the app
import { IHandlers, IProps } from './types';
import DatePicker from 'design-v2/common/DatePicker';
import moment, { Moment } from 'moment';
import { StepperSize } from 'design-v2/common/BidirectionalStepper/types';
import LocationService from 'external-services/location-service';
import SimpleSelect from 'design-v2/common/SimpleSelect';
import { useDispatch, useSelector } from 'react-redux';
import { DiaryRootState } from 'appRoot/rootReducer';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { serviceActions } from 'app/reducers/service/slice';
import { scheduleActions } from 'app/reducers/schedule/slice';
import { useMediaQueryMatch } from 'app/common/useMatchMediaHook/use-media-match-hook';
import { floorplanBlockoutsActions } from 'floorplan-v2/app/reducers/blockouts/slice';
import ViewActionsService from 'app/actions/view.actions';
import { blockoutTypeEnums } from 'app/common/BlockoutsByService/types';
import FloorplanHeaderService from "floorplan-v2/app/services/FloorplanHeaderService";
import TimeChanger from './TimeChanger'
import { filterBookingActions } from 'booking-card-list/reducers/filterBooking/slice';
import DateUtilsService from 'external-services/date-utils-service';
import { MainViewType } from 'app/services/mainViewVisibility/types';
import { makeStyles } from '@material-ui/core/styles';
import { ISectionOptions } from 'external-services/section-groups-service/type';
import { Paper } from '@material-ui/core';
import LayoutsService from 'external-services/layouts-service/layouts.service';
import { IServiceItem } from 'app/app.types';
import { floorplanV2SectionsActions } from 'floorplan-v2/app/reducers/sections/slice';
import { sectionActions } from 'app/reducers/section/slice';
import WeekdaysSelector from '../WeekdaysSelector';
import AdminService from "external-services/diary-admin-service/admin.service";
import { LocaleService } from 'internal-services/locale/locale.service';

import classNames from 'classnames';
const useStyles = makeStyles((theme) => ({
  selectedDay: {
    width: '28px !important',
    height: '28px !important',
    margin: '4px 5px !important',
    color: 'white !important',
    backgroundColor: '#992662 !important',
  },
  closedDay: {
    backgroundColor: '#DFDFDF !important',
    color: '#808080 !important',
    borderRadius: '50% !important',
    width: '28px !important',
    height: '28px !important',
    margin: '4px !important',
    display: 'flex !important',
    alignItems: 'center !important',
    justifyContent: 'center !important',
    border: 'none !important',
    '&:hover': {
      backgroundColor: 'darkgrey !important',
    },
    '&.MuiPickersDay-daySelected': {
      backgroundColor: 'grey !important',
      color: 'white !important',
    },
  },
}));

const NS = 'FloorplanHeader';

export default function FloorplanHeader({ handleDateChanged, onServiceFilterChanged, onSectionFilterChanged, sectionOptions }: IProps & IHandlers) {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const classes = useStyles();

  const selectedFloorplanDate = useSelector((state: DiaryRootState) => state.scheduleReducer.selectedDate);
  const currentServiceViews = useSelector((state: DiaryRootState) => state.serviceReducer.currentServiceViews);
  const selectedSchedule = useSelector((state: DiaryRootState) => state.scheduleReducer.selectedSchedule);
  const couchReducedSchedules = useSelector((state: DiaryRootState) => state.scheduleReducer.couchReducedSchedules);
  const bookingView = useSelector((state: DiaryRootState) => state.settingReducer.bookingView);
  const [selectedMoment, setSelectedMoment] = useState<Moment>();
  const [selectedServiceIndex, setSelectedServiceIndex] = useState(-1);
  const isHandHeldDevice = LocationService.isIOS() || LocationService.isAndroid();

  const [selectedItemIndex, setSelectedItemIndex] = useState(-1);

  const serviceList = useMemo(() => {
    const result = FloorplanHeaderService.getServicesList(currentServiceViews);
    return result;
  }, [currentServiceViews]);

  // Create a map of closed days for quick lookup
  const closedDaysMap = useMemo(() => {
    const map = new Map();
    if (couchReducedSchedules) {
      couchReducedSchedules.forEach(schedule => {
        if (!schedule.isOpen) {
          // Convert dayName to day number (0 = Sunday, 1 = Monday, etc.)
          const dayMap: { [key: string]: number } = {
            'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3,
            'Thursday': 4, 'Friday': 5, 'Saturday': 6
          };
          const dayNumber = dayMap[schedule.dayName];
          if (dayNumber !== undefined) {
            map.set(dayNumber, true);
          }
        }
      });
    }
    return map;
  }, [couchReducedSchedules]);

  // Custom renderDay function to show closed days with grey background
  const renderDay = useCallback((day: any, selectedDate: any, dayInCurrentMonth: boolean, dayComponent: React.ReactElement) => {
    if (!day || !dayInCurrentMonth) {
      return dayComponent;
    }

    // Handle both Date objects and Moment objects
    const dayOfWeek = moment.isMoment(day) ? day.day() : day.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const dayNumber = moment.isMoment(day) ? day.date() : day.getDate();
    const isClosedDay = closedDaysMap.get(dayOfWeek);

    // Check if this day is selected
    const isSelected = selectedDate && moment.isMoment(selectedDate) ?
      moment(day).isSame(selectedDate, 'day') :
      moment(day).isSame(moment(selectedDate), 'day');

    if (isClosedDay) {
      return React.cloneElement(dayComponent, {
        className: `${dayComponent.props.className || ''} ${classes.closedDay}`.trim(),
        children: dayNumber,
      });
    }

    if (isSelected) {
      return React.cloneElement(dayComponent, {
        className: `${dayComponent.props.className || ''} ${classes.selectedDay}`.trim(),
        children: dayNumber,
      });
    }

    return dayComponent;

  }, [closedDaysMap, classes.closedDay, classes.selectedDay]);

  const sectionListVal = useMemo(() => {
    return sectionOptions?.map((service, index) => {
      return {
        name: service.name,
        value: index.toString(),
        rawData: service,
      };
    }) || [];
  }, [sectionOptions]);

  const sectionChanged = (section: ISectionOptions) => {
    dispatch(floorplanV2SectionsActions.setSelectedSectionIds(section?.sectionIds ? section.sectionIds : []));
    // when section is not selected the Overview
    if (!!section?.sectionIds) {
      dispatch(sectionActions.setSection(section));
    } else {
      dispatch(sectionActions.setSection(null));
    }
  };

  const handleSectionChanged = (section: ISectionOptions) => {

    sectionChanged(section)
    if (!section) {
      setSelectedItemIndex(-1)
    }
  }

  useEffect(() => {
    LocaleService.configureMomentLocale(); // ensure moment is in sync with the user's locale
    if (selectedFloorplanDate && (!selectedMoment || !DateUtilsService.isOnSameDate(selectedFloorplanDate, selectedMoment?.toDate()))) {
      setSelectedMoment(moment(selectedFloorplanDate));
    }
  }, [selectedFloorplanDate]);

  const handleSelectDate = useCallback(
    (selectedMoment: Moment) => {
      LocaleService.configureMomentLocale(); // ensure moment is in sync with the user's locale
      // Add 4 hours to the date, then it is not past day
      const newDateFromScheduleStartTime = DateUtilsService.getDateFromScheduleStartTime(selectedMoment.toDate());
      const newMom = moment(newDateFromScheduleStartTime);
      setSelectedMoment(newMom);

      /**
       * Sending data back to bookings.controller - to update the date in timeline view
       * Needs to be cleared to midnight to match the date picker in the timeline view
       */
      const timezoneId = AdminService.getVenueTimezoneId();
      const clearDate = DateUtilsService.getClearDate(selectedMoment.toDate(), timezoneId);
      handleDateChanged(clearDate);

      dispatch(scheduleActions.setSelectedDate(newDateFromScheduleStartTime));
      dispatch(floorplanBlockoutsActions.resetActiveAddBlockouts());
      ViewActionsService.onBlockoutFinished(blockoutTypeEnums.byTable);
    },
    []);

  useEffect(() => {
    setSelectedServiceIndex(0);
  }, [selectedMoment]);

  const handleSelectService = useCallback(
    async (serviceIdx: number) => {
      const selectedServiceView = currentServiceViews[serviceIdx];

      onServiceFilterChanged(serviceIdx);
      setSelectedServiceIndex(serviceIdx);
      // when selected option in service dropdown is not all
      if (!selectedServiceView.services && !selectedServiceView.service) {
        // when selected all in service dropdown, we need to set the selectedServiceView to null
        dispatch(serviceActions.setSelectedServiceView(null));
        dispatch(filterBookingActions.setFilterServiceView(null))
      } else {
        dispatch(serviceActions.setSelectedServiceView(selectedServiceView));
        dispatch(filterBookingActions.setFilterServiceView(selectedServiceView))
      }
    },
    [serviceList],
  );

  // hook to get the size of the device screen - md is from 768 px to 1024 px
  const isSmSize = useMediaQueryMatch('sm');
  const isMdSize = useMediaQueryMatch('md');
  const isLgSize = useMediaQueryMatch('lg');

  const isSmallScreen = isSmSize || isMdSize || isLgSize;

  const getDatePickerMinWidth = () => {
    if (isSmSize) {
      return 100;
    }
    if (isMdSize) {
      return 120;
    }
    if (isLgSize) {
      return 140;
    }
    return 220;
  };

  return (
    <div className={style.root}>
      <div className={style.selectionWrapper}>
        <div className={style.datePickerWrapper}>
          {selectedMoment && (
            <DatePicker
              pickerContainerClass={style.datePickerLargeMobileContainer}
              whiteBackground={false}
              minWidth={getDatePickerMinWidth()}
              datePreset={isSmSize ? 'day-month' : 'weekday-short'}
              currentDate={selectedMoment}
              handleDateUpdate={handleSelectDate}
              stepperSize={StepperSize.regular}
              isModalView={isHandHeldDevice}
              textAlign={'center'}
              isFloorPlan={true}
              showTodayAsDot={isSmallScreen}
              closeOnOuterClick={!isHandHeldDevice} // for modal view this feature should not work
              renderDay={renderDay}
            />
          )}
          {/* WeekdaysSelector under DatePicker */}
          <WeekdaysSelector
            onDateChange={(date) => handleSelectDate(moment(date))}
            className="floorplan-weekdays"
          />
        </div>
        {selectedSchedule?.isOpen && serviceList.length > 2 && <div className={style.childWrapper}>
          <SimpleSelect
            label="Service"
            list={serviceList}
            selectedItemIndex={selectedServiceIndex}
            handleSelectItem={handleSelectService}
            isFloorPlanDesign={true}
            alternateSelectionHighlight={true}
            largeFont={true}
          />
        </div>}
        {selectedSchedule?.isOpen && sectionListVal.length > 2 && bookingView !== MainViewType.FloorplanV2 &&
          <div className={style.childWrapper}>
            <SimpleSelect
              label={`View Section`}
              list={sectionListVal}
              selectedItemIndex={selectedItemIndex}
              handleSelectItem={(s) => {
                handleSectionChanged(sectionOptions[s])
                // ViewActionsService.onSelectSection(s);
                onSectionFilterChanged(s);
                setSelectedItemIndex(s)
              }}
              isFloorPlanDesign={true}
              alternateSelectionHighlight={true}
              largeFont={true}
            />
          </div>
        }
        {bookingView === MainViewType.FloorplanV2 && <TimeChanger setSelectedMoment={setSelectedMoment} selectedMoment={selectedMoment} selectedServiceIndex={selectedServiceIndex} handleDateChanged={handleDateChanged} />}
      </div>
    </div>
  );
}

